import { useCallback } from 'react';
import { FieldValues, UseFormGetValues, UseFormHandleSubmit } from 'react-hook-form';
import { differenceInDays } from 'date-fns';

import { GroupSelectOptionsInterface } from '@components/common';

import { useAppDispatch } from '@store/store';

import { setAggregationTypeOnMap, setLastKpiSourceData } from '@slices/kpi';

import { getTimeAggregationType } from '@utils/kpi-utils/common';
import {
	endOfDayInUTC,
	startOfDayInUTC,
} from '@utils/kpi-utils/date';
import { getDateSelectionParameters } from '@components/Kpi/Parameters/utils';

import { IExportKpiFromParamsDTO, IKpiChartsDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IKPISite, IKpiSourceData } from '@interfaces/kpi/kpiCommon';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { IGroupSelectItem } from '@components/common/GroupSelect/GroupSelect.types';
import { IGroupSelectOptionsKpi } from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import {
	ETechnologiesValues,
	EVendorsValues,
	TTechnologiesValues,
	TVendorsValues,
} from '@enums/common';

interface UseSubmitAndExportLogicProps {
	getValues: UseFormGetValues<FieldValues>;
	handleSubmit: UseFormHandleSubmit<FieldValues>;
	vendor: { name?: TVendorsValues; value: TVendorsValues };
	treeKpiIds: string[];
	treeCountersIds: string[];
	isMapMode: boolean;
	activeSiteMarkers: string[];
	activeCustomPolygons: string[];
	runProgress: (values: IKpiChartsDTO) => void;
	getCharts: (values: IKpiChartsDTO) => void;
	presetValue?: TSelectOptionType<string>;
	exportChartsFromParams?: (data: IExportKpiFromParamsDTO) => void;
}

export const useSubmitAndExportLogic = ({
	getValues,
	handleSubmit,
	vendor,
	treeKpiIds,
	treeCountersIds,
	isMapMode,
	activeSiteMarkers,
	activeCustomPolygons,
	runProgress,
	getCharts,
	presetValue,
	exportChartsFromParams,
}: UseSubmitAndExportLogicProps) => {
	const dispatch = useAppDispatch();

	const getSites = useCallback((options: Array<GroupSelectOptionsInterface>, technology: TTechnologiesValues) => {
		return options.reduce<Array<IKPISite>>((acc, option) => {
			const processItems = (items: Array<IGroupSelectItem>, parentSiteId: string): IKPISite => {
				const collectedCellIds: string[] = [];

				const collectCellIds = (items: Array<IGroupSelectItem>, siteId: string) => {
					items.forEach(item => {
						if (item.id) {
							collectedCellIds.push(item.id);
						}
						if (item.items) {
							collectCellIds(item.items, siteId);
						}
					});
				};

				collectCellIds(items, parentSiteId);

				return {
					cellIds: collectedCellIds,
					siteId: parentSiteId,
					technology,
				};
			};

			if (option.items.some((item: IGroupSelectItem) => item.category && item.items)) {
				option.items.forEach((subCategory: IGroupSelectItem) => {
					if (subCategory.category) {
						acc.push(processItems(subCategory.items || [], subCategory.category.name));
					}
				});
			} else {
				const cellIds = option.items.map((item: IGroupSelectItem) => item.id || '');

				acc.push({
					cellIds,
					siteId: option.category.name,
					technology,
				});
			}

			return acc;
		}, []);
	}, []);

	const getKpiIds = useCallback((options: Array<IGroupSelectOptionsKpi>) => {
		if (!options.length) return null;

		return options.reduce<string[]>((acc: string[], option: IGroupSelectOptionsKpi) => {
			const ids: string[] = option.categoryGroups.reduce((accIds: string[], categoryGroup): string[] => {
				for (const item of categoryGroup.items){
					accIds.push(item.id);
				}

				return accIds;
			}, []);

			return [...acc, ...ids];
		}, []);
	}, []);

	const prepareDataForSubmit = useCallback((values: FieldValues) => {
		const diffInDays = differenceInDays(values.to, values.from);

		const timeAggregationType = getTimeAggregationType(diffInDays);

		const sites: Array<IKPISite> = [
			...values.sitesT_4G_FDD.length
				? [...getSites(values.sitesT_4G_FDD, ETechnologiesValues.T_4G_FDD)]
				: [],
			...values.sitesT_4G_TDD.length
				? [...getSites(values.sitesT_4G_TDD, ETechnologiesValues.T_4G_TDD)]
				: [],
			...values.sitesT_5G.length
				? [...getSites(values.sitesT_5G, ETechnologiesValues.T_5G)]
				: [],
		];

		return {
			timeAggregationType,
			sites,
		};
	}, [getSites]);

	const onSubmit = useCallback((values: FieldValues) => {
		const { timeAggregationType, sites } = prepareDataForSubmit(values);
		const dateFromUTC = startOfDayInUTC(new Date(values.from));
		const dateToUTC = endOfDayInUTC(new Date(values.to));
		const vendorValue: TVendorsValues = vendor.name || vendor.value;
		const kpiIdsList = getKpiIds(values.kpi);
		const isOranVendor = vendorValue === EVendorsValues.oran;
		const isLimenetVendor = vendorValue === EVendorsValues.limenet;
		const preparedSites = sites.filter(site => site?.cellIds?.length);
		const preparedKpiIds = Array.from(new Set(
			(treeKpiIds?.length || treeCountersIds?.length)
				? [...treeKpiIds, ...treeCountersIds]
				: kpiIdsList,
		));
		const preparedTimeAggregationType = isOranVendor ? undefined : timeAggregationType;
		const isAutomaticGranularity = isOranVendor ? values.isAutomaticGranularity : undefined;
		const granularity = isOranVendor ? values.granularity?.value : undefined;

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const submitData: IKpiChartsDTO = {
			vendor: vendorValue,
			aggregationType: values.aggregationType,
			kpiIds: preparedKpiIds,
			sites: preparedSites,
			timeAggregationType: preparedTimeAggregationType,
			isAutomaticGranularity,
			granularity,
			...dateSelectionParameters,
		};

		const kpiSourceData: IKpiSourceData = {
			...submitData,
			selectedSiteIds: activeSiteMarkers,
			selectedPolygonIds: activeCustomPolygons,
			presetId: (presetValue?.id || '').toString(),
		};

		dispatch(setAggregationTypeOnMap(values.aggregationType));

		if (isMapMode) {
			dispatch(setLastKpiSourceData(kpiSourceData));
		}

		if (isOranVendor || isLimenetVendor) {
			runProgress(submitData);
		} else {
			getCharts(submitData);
		}
	}, [
		presetValue?.id,
		prepareDataForSubmit,
		vendor,
		treeKpiIds,
		treeCountersIds,
		getKpiIds,
		activeSiteMarkers,
		activeCustomPolygons,
		isMapMode,
		dispatch,
		runProgress,
		getCharts,
	]);

	const exportHandler = useCallback((data: IExportFileDTO) => {
		const values = getValues();
		const { sites } = prepareDataForSubmit(values);
		const dateFromUTC = startOfDayInUTC(new Date(values.from));
		const dateToUTC = endOfDayInUTC(new Date(values.to));
		const vendorValue: TVendorsValues = vendor.name || vendor.value;
		const isOranVendor = vendorValue === EVendorsValues.oran;
		const kpiIdsList = getKpiIds(values.kpi);
		const preparedSites = sites.filter(site => site?.cellIds?.length);
		const preparedKpiIds = Array.from(new Set(
			(treeKpiIds?.length || treeCountersIds?.length)
				? [...treeKpiIds, ...treeCountersIds]
				: kpiIdsList,
		));

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const exportData: IExportKpiFromParamsDTO = {
			vendor: vendorValue,
			aggregationType: values.aggregationType,
			kpiIds: preparedKpiIds,
			sites: preparedSites,
			isAutomaticGranularity: values.isAutomaticGranularity,
			granularity: values.granularity?.value,
			fileType: data.fileType,
			...dateSelectionParameters,

		};

		exportChartsFromParams?.(exportData);
	}, [
		exportChartsFromParams,
		getValues,
		prepareDataForSubmit,
		vendor,
		treeKpiIds,
		treeCountersIds,
		getKpiIds,
	]);

	return {
		onSubmit: handleSubmit(onSubmit),
		exportHandler,
		prepareDataForSubmit,
		getKpiIds,
	};
};
